import type { LocationHistory } from "@ots-frontend/ots-data";
import { otsMapPosition, type OtsMapPosition } from "../../components/OtsGoogleMap";
import type { ActivityDriver, ActivityEntry } from "./activity-types";

export type ActivityDayStopKind = "pickup" | "delivery" | "visit";

/** A place the driver was during the day, in time order. */
export type ActivityDayStop = {
  id: string;
  /** Its place in the day, from 1. */
  order: number;
  kind: ActivityDayStopKind;
  time: number;
  label: string;
  position: OtsMapPosition;
  /** The row it came from, to open it. */
  entryKey: string;
};

const stopsOf = (entry: ActivityEntry): Omit<ActivityDayStop, "order">[] => {
  const stops: Omit<ActivityDayStop, "order">[] = [];
  const add = (id: string, kind: ActivityDayStopKind, time: number | undefined, label: string, position: OtsMapPosition | undefined) => {
    if (time != null && position) stops.push({ id, kind, time, label, position, entryKey: entry.key });
  };
  const { pickup, delivery } = entry;
  add(`${entry.key}-pickup`, "pickup", entry.tta?.pickupDttm ?? pickup?.createDttm, `Pickup · ${pickup?.siteName ?? "site"}`, otsMapPosition(pickup?.latCoord, pickup?.longCoord));
  add(`${entry.key}-delivery`, "delivery", entry.tta?.deliveryDttm ?? delivery?.createDttm, `Delivery · ${delivery?.siteName ?? "site"}`, otsMapPosition(delivery?.latCoord, delivery?.longCoord));
  // A visit matched to a ticket is drawn by the ticket; only visits on their own rows are stops of their own.
  if (entry.kind === "visit" && entry.geofenceEvent) {
    const visit = entry.geofenceEvent;
    add(`${entry.key}-visit`, "visit", visit.inDttm, `Site visit · ${visit.siteName ?? "site"}`, otsMapPosition(visit.latCoord, visit.longCoord));
  }
  return stops;
};

/** Everywhere the driver stopped during the day, numbered in time order. */
export const driverDayStops = (driver: ActivityDriver): ActivityDayStop[] =>
  driver.shifts.flatMap((shift) => shift.entries.flatMap(stopsOf))
    .sort((left, right) => left.time - right.time)
    .map((stop, index) => ({ ...stop, order: index + 1 }));

/** A shift's recorded road, oldest first, without empty fixes. */
export const shiftBreadcrumbs = (history: LocationHistory | undefined): OtsMapPosition[] =>
  [...(history?.driverLocationList ?? [])]
    .sort((left, right) => left.createDttm - right.createDttm)
    .flatMap((location) => {
      const position = otsMapPosition(location.latCoord, location.longCoord);
      return position ? [position] : [];
    });
